import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Minimal WYSIWYG layout designer inspired by List & Label style.
// Stores elements in `layoutFieldsJson` and generates `bodyHtml` using absolute positioning (mm units).

const PX_PER_MM = 96 / 25.4; // ~3.7795

// Legacy designer stored coordinates in a 595x842 canvas (A4 @ 72dpi points).
const LEGACY_CANVAS = { w: 595, h: 842 };

const uid = () => `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function mmToPx(mm, zoom) {
  return (Number(mm) || 0) * PX_PER_MM * zoom;
}

function pxToMm(px, zoom) {
  return (px / (PX_PER_MM * zoom));
}

function safeText(s) {
  return (s ?? '').toString();
}

function escapeHtml(str) {
  return safeText(str)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function buildHtmlFromElements(elements, page) {
  const { widthMm, heightMm, marginMm } = page;

  const css = `
  .ll-page { position: relative; width: ${widthMm}mm; height: ${heightMm}mm; box-sizing: border-box; }
  .ll-el { position: absolute; box-sizing: border-box; }
  .ll-text { white-space: pre-wrap; }
  .ll-rect { border: 1px solid rgba(0,0,0,0.25); border-radius: 2px; }
  .ll-line-h { border-top: 1px solid rgba(0,0,0,0.35); }
  .ll-line-v { border-left: 1px solid rgba(0,0,0,0.35); }
  img.ll-img { width: 100%; height: 100%; object-fit: contain; }
  `;

  const elHtml = (elements || [])
    .filter(e => e && e.visible !== false)
    .map(e => {
      const x = Number(e.xMm) || 0;
      const y = Number(e.yMm) || 0;
      const w = Number(e.wMm) || 10;
      const h = Number(e.hMm) || 5;

      const styleBase = `left:${x}mm; top:${y}mm; width:${w}mm; height:${h}mm;`;

      if (e.type === 'text') {
        const fontSize = Number(e.fontSizePx) || 12;
        const color = e.color || '#111';
        const align = e.align || 'left';
        const weight = e.bold ? 700 : 400;
        const italic = e.italic ? 'italic' : 'normal';
        const content = e.contentType === 'field' ? `{{${e.fieldKey}}}` : escapeHtml(e.text || '');
        return `<div class="ll-el ll-text" style="${styleBase} font-size:${fontSize}px; color:${color}; text-align:${align}; font-weight:${weight}; font-style:${italic};">${content}</div>`;
      }

      if (e.type === 'image') {
        const src = e.srcType === 'field' ? `{{${e.fieldKey}}}` : escapeHtml(e.src || '');
        return `<div class="ll-el" style="${styleBase}"><img class="ll-img" src="${src}" alt="" /></div>`;
      }

      if (e.type === 'rect') {
        return `<div class="ll-el ll-rect" style="${styleBase}"></div>`;
      }

      if (e.type === 'line') {
        const dir = e.direction || 'h';
        return `<div class="ll-el ${dir === 'v' ? 'll-line-v' : 'll-line-h'}" style="${styleBase}"></div>`;
      }

      if (e.type === 'linesTable') {
        // Render table placeholder (backend will inject full table markup)
        return `<div class="ll-el" style="${styleBase} overflow:hidden;">{{LinesTable}}</div>`;
      }

      if (e.type === 'html') {
        const html = e.htmlType === 'field' ? `{{${e.fieldKey}}}` : (e.html || '');
        return `<div class="ll-el" style="${styleBase}">${html}</div>`;
      }

      return '';
    })
    .join('\n');

  const body = `
  <style>${css}</style>
  <div class="ll-page" style="padding:${marginMm}mm;">${elHtml}</div>
  `;

  return body;
}

function getLayoutProp(layout, camelKey, pascalKey) {
  if (!layout) return undefined;
  if (layout[camelKey] != null) return layout[camelKey];
  if (layout[pascalKey] != null) return layout[pascalKey];
  return undefined;
}

function legacyPxToMmX(px, page) {
  const p = Number(px) || 0;
  return (p / LEGACY_CANVAS.w) * (Number(page?.widthMm) || 210);
}

function legacyPxToMmY(px, page) {
  const p = Number(px) || 0;
  return (p / LEGACY_CANVAS.h) * (Number(page?.heightMm) || 297);
}

function convertLegacyFieldsToElements(legacyFields, page) {
  const fieldIdToFieldKey = {
    // Company
    companyName: 'CompanyName',
    companyNif: 'CompanyNIF',
    companyAddress: 'CompanyAddress',
    companyPhone: 'CompanyPhone',
    companyEmail: 'CompanyEmail',
    companyLogo: 'CompanyLogoUrl',

    // Client
    clientName: 'ClientName',
    clientNif: 'ClientNIF',
    clientAddress: 'ClientAddress',
    clientPostalCode: 'ClientPostalCode',

    // Document
    documentType: 'DocumentType',
    invoiceNumber: 'InvoiceNumber',
    receiptNumber: 'InvoiceNumber',
    issueDate: 'IssueDate',
    dueDate: 'DueDate',
    paymentDate: 'IssueDate',
    paymentMethod: 'PaymentMethod',
    status: 'Status',

    // Totals
    subtotal: 'Subtotal',
    totalBruto: 'Subtotal',
    totalLiquido: 'Subtotal',
    totalIva: 'TaxAmount',
    totalFinal: 'Total',
    totalAPagar: 'Total',
    total: 'Total',

    // Footer
    footerText: 'FooterText',
    notes: 'Notes',

    // Tables
    linesTable: 'LinesTable',
  };

  const zones = { header: { y: 0, h: 220 }, body: { y: 220, h: 420 }, footer: { y: 640, h: 200 } };

  const safe = Array.isArray(legacyFields) ? legacyFields : [];
  return safe.map((f, i) => {
    const legacyType = (f?.type || '').toString();
    const legacyId = (f?.id || f?.fieldId || `legacy_${i}`).toString();

    const zone = zones[f?.zone] || zones.body;
    const xPx = (f?.x != null ? f.x : 10);
    const yPx = (f?.y != null ? f.y : (zone.y + 10 + (Number(f?.order) || 0) * 18));
    const wPx = (f?.w != null ? f.w : 140);
    const hPx = (f?.h != null ? f.h : 16);

    const xMm = Number(legacyPxToMmX(xPx, page).toFixed(2));
    const yMm = Number(legacyPxToMmY(yPx, page).toFixed(2));
    const wMm = Number(legacyPxToMmX(wPx, page).toFixed(2));
    const hMm = Number(legacyPxToMmY(hPx, page).toFixed(2));

    const fieldKey = fieldIdToFieldKey[legacyId];
    const label = f?.label || legacyId;

    // Map legacy types to new types
    if (legacyType === 'image') {
      if (fieldKey) {
        return { id: `legacy_${legacyId}_${i}`, type: 'image', visible: f?.visible !== false, xMm, yMm, wMm: Math.max(8, wMm), hMm: Math.max(8, hMm), srcType: 'field', fieldKey };
      }
      return { id: `legacy_${legacyId}_${i}`, type: 'image', visible: f?.visible !== false, xMm, yMm, wMm: Math.max(8, wMm), hMm: Math.max(8, hMm), srcType: 'static', src: '' };
    }

    if (legacyType === 'line' || legacyId === 'footerLine' || legacyId === 'headerLine') {
      const direction = wPx >= hPx ? 'h' : 'v';
      return { id: `legacy_${legacyId}_${i}`, type: 'line', visible: f?.visible !== false, direction, xMm, yMm, wMm: Math.max(2, wMm), hMm: Math.max(1, hMm) };
    }

    if (legacyId === 'linesTable' || legacyType === 'table') {
      if (legacyId === 'linesTable') {
        return { id: `legacy_${legacyId}_${i}`, type: 'linesTable', visible: f?.visible !== false, xMm, yMm, wMm: Math.max(20, wMm), hMm: Math.max(10, hMm) };
      }

      // Unknown tables: show as a box so user can reposition.
      return { id: `legacy_${legacyId}_${i}`, type: 'rect', visible: f?.visible !== false, xMm, yMm, wMm: Math.max(20, wMm), hMm: Math.max(10, hMm), _label: label };
    }

    if (legacyType === 'block' || legacyType === 'column') {
      return { id: `legacy_${legacyId}_${i}`, type: 'rect', visible: f?.visible !== false, xMm, yMm, wMm: Math.max(10, wMm), hMm: Math.max(6, hMm), _label: label };
    }

    // Default: text
    if (fieldKey) {
      return { id: `legacy_${legacyId}_${i}`, type: 'text', visible: f?.visible !== false, xMm, yMm, wMm: Math.max(10, wMm), hMm: Math.max(5, hMm), contentType: 'field', fieldKey, fontSizePx: 11 };
    }
    return { id: `legacy_${legacyId}_${i}`, type: 'text', visible: f?.visible !== false, xMm, yMm, wMm: Math.max(10, wMm), hMm: Math.max(5, hMm), contentType: 'static', text: label, fontSizePx: 10, color: '#334155' };
  });
}

const defaultFields = [
  // Company
  { key: 'CompanyName', label: 'Empresa - Nome', group: 'Empresa' },
  { key: 'CompanyNIF', label: 'Empresa - NIF', group: 'Empresa' },
  { key: 'CompanyAddress', label: 'Empresa - Morada', group: 'Empresa' },
  { key: 'CompanyPhone', label: 'Empresa - Telefone', group: 'Empresa' },
  { key: 'CompanyEmail', label: 'Empresa - Email', group: 'Empresa' },
  { key: 'CompanyLogoUrl', label: 'Empresa - Logo (URL)', group: 'Empresa' },

  // Client
  { key: 'ClientName', label: 'Cliente - Nome', group: 'Cliente' },
  { key: 'ClientNIF', label: 'Cliente - NIF', group: 'Cliente' },
  { key: 'ClientAddress', label: 'Cliente - Morada', group: 'Cliente' },
  { key: 'ClientPostalCode', label: 'Cliente - CP', group: 'Cliente' },
  { key: 'ClientEmail', label: 'Cliente - Email', group: 'Cliente' },

  // Document
  { key: 'DocumentType', label: 'Documento - Tipo', group: 'Documento' },
  { key: 'InvoiceNumber', label: 'Documento - Nº Completo', group: 'Documento' },
  { key: 'InvoiceNumberOnly', label: 'Documento - Nº', group: 'Documento' },
  { key: 'IssueDate', label: 'Documento - Data Emissão', group: 'Documento' },
  { key: 'DueDate', label: 'Documento - Data Vencimento', group: 'Documento' },
  { key: 'Status', label: 'Documento - Estado', group: 'Documento' },
  { key: 'PaymentMethod', label: 'Documento - Pagamento', group: 'Documento' },

  // Totals
  { key: 'Subtotal', label: 'Totais - Subtotal', group: 'Totais' },
  { key: 'TaxAmount', label: 'Totais - IVA', group: 'Totais' },
  { key: 'Total', label: 'Totais - Total', group: 'Totais' },

  // Other
  { key: 'FooterText', label: 'Rodapé - Texto', group: 'Rodapé' },
  { key: 'Notes', label: 'Notas', group: 'Outros' },

  // Prebuilt
  { key: 'LinesTable', label: 'Tabela Linhas (HTML)', group: 'Tabela' },
];

export default function LayoutDesignerPro({ isDarkMode, layout, onClose, onApply }) {
  const canvasRef = useRef(null);
  const dragState = useRef(null);

  const [zoom, setZoom] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [elements, setElements] = useState([]);

  const page = useMemo(() => {
    // A4 default; keep simple
    const paper = (layout?.paperSize || layout?.pageSize || 'A4').toUpperCase();
    if (paper === 'A5') return { widthMm: 148, heightMm: 210, marginMm: 10 };
    if (paper === 'LETTER') return { widthMm: 216, heightMm: 279, marginMm: 10 };
    return { widthMm: 210, heightMm: 297, marginMm: 10 };
  }, [layout]);

  const theme = useMemo(() => {
    if (isDarkMode) {
      return {
        appBg: '#0b1220',
        panelBg: '#0f172a',
        panelBg2: '#111827',
        border: '#24324f',
        text: '#e5e7eb',
        text2: 'rgba(229,231,235,0.75)',
        btnBg: '#111827',
        btnBorder: '#334155',
        btnText: '#e5e7eb',
        canvasBg: '#0b1220',
      };
    }
    return {
      appBg: '#f3f4f6',
      panelBg: '#ffffff',
      panelBg2: '#ffffff',
      border: '#e5e7eb',
      text: '#0f172a',
      text2: 'rgba(15,23,42,0.75)',
      btnBg: '#ffffff',
      btnBorder: '#cbd5e1',
      btnText: '#0f172a',
      canvasBg: '#eef2f7',
    };
  }, [isDarkMode]);

  useEffect(() => {
    // Load from layoutFieldsJson if present
    try {
      const raw = getLayoutProp(layout, 'layoutFieldsJson', 'LayoutFieldsJson');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const first = parsed[0] || {};
          const isProFormat = (first.type && (first.xMm != null || first.yMm != null || first.wMm != null || first.hMm != null));
          if (isProFormat) {
            setElements(parsed.map(e => ({ id: e.id || uid(), visible: e.visible !== false, ...e })));
            return;
          }

          // Legacy format (AdminBillingPage designer)
          const legacyLooksRight = (first.zone != null || first.fieldId != null || first.id != null || first.w != null || first.h != null);
          if (legacyLooksRight) {
            const converted = convertLegacyFieldsToElements(parsed, page);
            setElements(converted);
            return;
          }
        }

        // Empty array in legacy layouts: fall through to defaults
      }
    } catch {
      // ignore
    }

    // default starter
    setElements([
      { id: uid(), type: 'image', xMm: 10, yMm: 10, wMm: 50, hMm: 18, srcType: 'field', fieldKey: 'CompanyLogoUrl' },
      { id: uid(), type: 'text', xMm: 10, yMm: 32, wMm: 90, hMm: 10, contentType: 'field', fieldKey: 'CompanyName', fontSizePx: 16, bold: true },
      { id: uid(), type: 'text', xMm: 10, yMm: 42, wMm: 90, hMm: 20, text: 'NIF: {{CompanyNIF}}\n{{CompanyAddress}}\n{{CompanyPhone}}\n{{CompanyEmail}}', contentType: 'static', fontSizePx: 10 },
      { id: uid(), type: 'text', xMm: 130, yMm: 10, wMm: 70, hMm: 15, text: '{{DocumentType}} {{InvoiceNumberOnly}}\nData: {{IssueDate}}', contentType: 'static', align: 'right', fontSizePx: 12, bold: true },
      { id: uid(), type: 'rect', xMm: 10, yMm: 68, wMm: 190, hMm: 25 },
      { id: uid(), type: 'text', xMm: 12, yMm: 70, wMm: 90, hMm: 20, text: 'Cliente:\n{{ClientName}}\nNIF: {{ClientNIF}}', contentType: 'static', fontSizePx: 10 },
      { id: uid(), type: 'linesTable', xMm: 10, yMm: 98, wMm: 190, hMm: 120 },
      { id: uid(), type: 'text', xMm: 130, yMm: 225, wMm: 70, hMm: 20, text: 'Subtotal: {{Subtotal}}\nIVA: {{TaxAmount}}\nTOTAL: {{Total}}', contentType: 'static', align: 'right', bold: true },
      { id: uid(), type: 'line', direction: 'h', xMm: 10, yMm: 270, wMm: 190, hMm: 1 },
      { id: uid(), type: 'text', xMm: 10, yMm: 272, wMm: 190, hMm: 15, contentType: 'field', fieldKey: 'FooterText', align: 'center', fontSizePx: 9, color: '#666' },
    ]);
  }, [layout, page]);

  const selected = useMemo(() => (elements || []).find(e => e.id === selectedId) || null, [elements, selectedId]);

  const groupedFields = useMemo(() => {
    const groups = {};
    for (const f of defaultFields) {
      groups[f.group] = groups[f.group] || [];
      groups[f.group].push(f);
    }
    return groups;
  }, []);

  const addElement = (type) => {
    const base = { id: uid(), type, visible: true, xMm: 10, yMm: 10, wMm: 40, hMm: 10 };
    if (type === 'text') base.text = 'Texto';
    if (type === 'line') { base.direction = 'h'; base.hMm = 1; base.wMm = 80; }
    if (type === 'image') { base.wMm = 40; base.hMm = 20; base.src = ''; }
    if (type === 'rect') { base.wMm = 60; base.hMm = 20; }
    if (type === 'linesTable') { base.wMm = 180; base.hMm = 80; }
    setElements(prev => [...prev, base]);
    setSelectedId(base.id);
  };

  const updateSelected = (patch) => {
    if (!selected) return;
    setElements(prev => prev.map(e => e.id === selected.id ? { ...e, ...patch } : e));
  };

  const deleteSelected = () => {
    if (!selected) return;
    setElements(prev => prev.filter(e => e.id !== selected.id));
    setSelectedId(null);
  };

  const onMouseDownEl = (e, el, mode) => {
    e.stopPropagation();
    setSelectedId(el.id);

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const start = { xMm: el.xMm, yMm: el.yMm, wMm: el.wMm, hMm: el.hMm };

    dragState.current = { mode, id: el.id, startX, startY, start, rect };
  };

  const onMouseMove = (e) => {
    const st = dragState.current;
    if (!st) return;

    const dxPx = e.clientX - st.startX;
    const dyPx = e.clientY - st.startY;
    const dxMm = pxToMm(dxPx, zoom);
    const dyMm = pxToMm(dyPx, zoom);

    setElements(prev => prev.map(el => {
      if (el.id !== st.id) return el;

      if (st.mode === 'move') {
        const nx = clamp((Number(st.start.xMm) || 0) + dxMm, 0, page.widthMm - 1);
        const ny = clamp((Number(st.start.yMm) || 0) + dyMm, 0, page.heightMm - 1);
        return { ...el, xMm: Number(nx.toFixed(2)), yMm: Number(ny.toFixed(2)) };
      }

      if (st.mode === 'resize-se') {
        const nw = clamp((Number(st.start.wMm) || 1) + dxMm, 2, page.widthMm);
        const nh = clamp((Number(st.start.hMm) || 1) + dyMm, 2, page.heightMm);
        return { ...el, wMm: Number(nw.toFixed(2)), hMm: Number(nh.toFixed(2)) };
      }

      return el;
    }));
  };

  const onMouseUp = () => {
    dragState.current = null;
  };

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  });

  const exportToLayout = () => {
    const json = JSON.stringify(elements, null, 0);
    const bodyHtml = buildHtmlFromElements(elements, page);
    onApply?.({
      ...layout,
      layoutFieldsJson: json,
      bodyHtml,
      customCss: layout?.customCss || '',
    });
    toast.success('Layout aplicado (LayoutFieldsJson + BodyHtml)');
  };

  const canvasW = mmToPx(page.widthMm, zoom);
  const canvasH = mmToPx(page.heightMm, zoom);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: theme.appBg, display: 'flex', color: theme.text }}>
      {/* Left: toolbox */}
      <div style={{ width: 300, borderRight: `1px solid ${theme.border}`, padding: 14, overflow: 'auto', background: theme.panelBg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 800, color: theme.text }}>🧩 Designer de Layout (Pro)</div>
          <button onClick={onClose} style={{ border: 'none', background: '#ef4444', color: '#fff', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ marginTop: 12, padding: 10, background: theme.panelBg2, borderRadius: 10, border: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 8, color: theme.text2 }}>Ferramentas</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button onClick={() => addElement('text')} style={{ padding: 8, borderRadius: 8, border: `1px solid ${theme.btnBorder}`, background: theme.btnBg, color: theme.btnText, cursor: 'pointer', fontWeight: 700 }}>T Texto</button>
            <button onClick={() => addElement('image')} style={{ padding: 8, borderRadius: 8, border: `1px solid ${theme.btnBorder}`, background: theme.btnBg, color: theme.btnText, cursor: 'pointer', fontWeight: 700 }}>🖼️ Imagem</button>
            <button onClick={() => addElement('line')} style={{ padding: 8, borderRadius: 8, border: `1px solid ${theme.btnBorder}`, background: theme.btnBg, color: theme.btnText, cursor: 'pointer', fontWeight: 700 }}>／ Linha</button>
            <button onClick={() => addElement('rect')} style={{ padding: 8, borderRadius: 8, border: `1px solid ${theme.btnBorder}`, background: theme.btnBg, color: theme.btnText, cursor: 'pointer', fontWeight: 700 }}>▭ Caixa</button>
            <button onClick={() => addElement('linesTable')} style={{ padding: 8, borderRadius: 8, border: `1px solid ${theme.btnBorder}`, background: theme.btnBg, color: theme.btnText, cursor: 'pointer', gridColumn: '1 / -1', fontWeight: 700 }}>▦ Tabela Linhas</button>
          </div>
        </div>

        <div style={{ marginTop: 12, padding: 10, background: theme.panelBg2, borderRadius: 10, border: `1px solid ${theme.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, opacity: 0.9, color: theme.text2 }}>Zoom</div>
            <div style={{ fontSize: 12, opacity: 0.9, color: theme.text2 }}>{Math.round(zoom * 100)}%</div>
          </div>
          <input type="range" min="0.5" max="1.8" step="0.05" value={zoom} onChange={e => setZoom(Number(e.target.value))} style={{ width: '100%' }} />
        </div>

        <div style={{ marginTop: 12, padding: 10, background: theme.panelBg2, borderRadius: 10, border: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 8, color: theme.text2 }}>Campos (clique para inserir)</div>
          {Object.keys(groupedFields).map(g => (
            <div key={g} style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 6, color: theme.text }}>{g}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {groupedFields[g].map(f => (
                  <button
                    key={f.key}
                    onClick={() => {
                      const el = { id: uid(), type: 'text', xMm: 10, yMm: 10, wMm: 80, hMm: 8, contentType: 'field', fieldKey: f.key, fontSizePx: 11 };
                      setElements(prev => [...prev, el]);
                      setSelectedId(el.id);
                    }}
                    style={{ textAlign: 'left', padding: '6px 8px', borderRadius: 8, border: `1px solid ${theme.btnBorder}`, background: theme.btnBg, color: theme.btnText, cursor: 'pointer' }}
                    title={`{{${f.key}}}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button onClick={exportToLayout} style={{ flex: 1, padding: 10, borderRadius: 10, border: 'none', background: '#22c55e', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>Aplicar</button>
        </div>
      </div>

      {/* Center: canvas */}
      <div style={{ flex: 1, padding: 14, overflow: 'auto', background: theme.canvasBg }}>
        <div
          ref={canvasRef}
          onMouseDown={() => setSelectedId(null)}
          style={{
            width: canvasW,
            height: canvasH,
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
            position: 'relative',
            margin: '0 auto',
            backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.04) 1px, transparent 1px)',
            backgroundSize: `${mmToPx(5, zoom)}px ${mmToPx(5, zoom)}px`,
          }}
        >
          {(elements || []).map(el => {
            const x = mmToPx(el.xMm, zoom);
            const y = mmToPx(el.yMm, zoom);
            const w = mmToPx(el.wMm, zoom);
            const h = mmToPx(el.hMm, zoom);
            const isSel = el.id === selectedId;

            return (
              <div
                key={el.id}
                onMouseDown={(e) => onMouseDownEl(e, el, 'move')}
                style={{
                  position: 'absolute',
                  left: x,
                  top: y,
                  width: w,
                  height: h,
                  border: isSel ? '2px solid #3b82f6' : '1px dashed rgba(0,0,0,0.18)',
                  borderRadius: 4,
                  padding: 2,
                  overflow: 'hidden',
                  cursor: 'move',
                  background: el.type === 'rect' ? 'rgba(0,0,0,0.02)' : 'transparent',
                }}
              >
                {el.type === 'text' && (
                  <div style={{
                    fontSize: el.fontSizePx || 12,
                    fontWeight: el.bold ? 700 : 400,
                    fontStyle: el.italic ? 'italic' : 'normal',
                    color: el.color || '#111',
                    textAlign: el.align || 'left',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {el.contentType === 'field' ? `{{${el.fieldKey}}}` : (el.text || '')}
                  </div>
                )}

                {el.type === 'image' && (
                  <div style={{ fontSize: 12, opacity: 0.75 }}>
                    🖼️ {el.srcType === 'field' ? `{{${el.fieldKey}}}` : (el.src || '(url)')}
                  </div>
                )}

                {el.type === 'line' && (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    borderTop: el.direction !== 'v' ? '2px solid rgba(0,0,0,0.25)' : 'none',
                    borderLeft: el.direction === 'v' ? '2px solid rgba(0,0,0,0.25)' : 'none'
                  }} />
                )}

                {el.type === 'rect' && (
                  <div style={{ width: '100%', height: '100%', border: '1px solid rgba(0,0,0,0.25)', position: 'relative' }}>
                    {el._label && (
                      <div style={{ position: 'absolute', left: 4, top: 2, fontSize: 10, color: '#334155', opacity: 0.85, background: 'rgba(255,255,255,0.8)', padding: '1px 4px', borderRadius: 4 }}>
                        {String(el._label)}
                      </div>
                    )}
                  </div>
                )}

                {el.type === 'linesTable' && (
                  <div style={{ fontSize: 12, opacity: 0.8 }}>▦ {'{{LinesTable}}'}</div>
                )}

                {isSel && (
                  <div
                    onMouseDown={(e) => onMouseDownEl(e, el, 'resize-se')}
                    style={{
                      position: 'absolute',
                      right: -2,
                      bottom: -2,
                      width: 10,
                      height: 10,
                      background: '#3b82f6',
                      borderRadius: 2,
                      cursor: 'nwse-resize'
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: properties */}
      <div style={{ width: 340, borderLeft: `1px solid ${theme.border}`, padding: 14, overflow: 'auto', background: theme.panelBg }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>⚙️ Propriedades</div>

        {!selected ? (
          <div style={{ fontSize: 13, opacity: 0.8 }}>Selecione um elemento no documento.</div>
        ) : (
          <>
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>Tipo: <strong>{selected.type}</strong></div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700 }}>X (mm)</label>
                <input type="number" value={selected.xMm} onChange={e => updateSelected({ xMm: Number(e.target.value) })} style={{ width: '100%' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700 }}>Y (mm)</label>
                <input type="number" value={selected.yMm} onChange={e => updateSelected({ yMm: Number(e.target.value) })} style={{ width: '100%' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700 }}>W (mm)</label>
                <input type="number" value={selected.wMm} onChange={e => updateSelected({ wMm: Number(e.target.value) })} style={{ width: '100%' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700 }}>H (mm)</label>
                <input type="number" value={selected.hMm} onChange={e => updateSelected({ hMm: Number(e.target.value) })} style={{ width: '100%' }} />
              </div>
            </div>

            {selected.type === 'text' && (
              <>
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 700 }}>Conteúdo</label>
                  <select value={selected.contentType || 'static'} onChange={e => updateSelected({ contentType: e.target.value })} style={{ width: '100%' }}>
                    <option value="static">Texto</option>
                    <option value="field">Campo</option>
                  </select>
                </div>

                {selected.contentType === 'field' ? (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Campo</label>
                    <select value={selected.fieldKey || 'CompanyName'} onChange={e => updateSelected({ fieldKey: e.target.value })} style={{ width: '100%' }}>
                      {defaultFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                  </div>
                ) : (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Texto</label>
                    <textarea value={selected.text || ''} onChange={e => updateSelected({ text: e.target.value })} rows={4} style={{ width: '100%' }} />
                    <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>Pode usar tokens: {'{{CompanyName}}'}, {'{{ClientName}}'}, {'{{Total}}'}, {'{{FooterText}}'}, etc.</div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Fonte (px)</label>
                    <input type="number" value={selected.fontSizePx || 12} onChange={e => updateSelected({ fontSizePx: Number(e.target.value) })} style={{ width: '100%' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Cor</label>
                    <input type="color" value={selected.color || '#111111'} onChange={e => updateSelected({ color: e.target.value })} style={{ width: '100%', height: 34 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Alinhar</label>
                    <select value={selected.align || 'left'} onChange={e => updateSelected({ align: e.target.value })} style={{ width: '100%' }}>
                      <option value="left">Esquerda</option>
                      <option value="center">Centro</option>
                      <option value="right">Direita</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'end', gap: 10 }}>
                    <label style={{ fontSize: 12 }}><input type="checkbox" checked={!!selected.bold} onChange={e => updateSelected({ bold: e.target.checked })} /> Negrito</label>
                    <label style={{ fontSize: 12 }}><input type="checkbox" checked={!!selected.italic} onChange={e => updateSelected({ italic: e.target.checked })} /> Itálico</label>
                  </div>
                </div>
              </>
            )}

            {selected.type === 'image' && (
              <>
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 700 }}>Fonte</label>
                  <select value={selected.srcType || 'field'} onChange={e => updateSelected({ srcType: e.target.value })} style={{ width: '100%' }}>
                    <option value="field">Campo</option>
                    <option value="static">URL</option>
                  </select>
                </div>

                {selected.srcType === 'field' ? (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Campo</label>
                    <select value={selected.fieldKey || 'CompanyLogoUrl'} onChange={e => updateSelected({ fieldKey: e.target.value })} style={{ width: '100%' }}>
                      {defaultFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                  </div>
                ) : (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>URL</label>
                    <input value={selected.src || ''} onChange={e => updateSelected({ src: e.target.value })} style={{ width: '100%' }} placeholder="https://..." />
                  </div>
                )}
              </>
            )}

            {selected.type === 'line' && (
              <div style={{ marginTop: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 700 }}>Direção</label>
                <select value={selected.direction || 'h'} onChange={e => updateSelected({ direction: e.target.value })} style={{ width: '100%' }}>
                  <option value="h">Horizontal</option>
                  <option value="v">Vertical</option>
                </select>
              </div>
            )}

            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button onClick={deleteSelected} style={{ flex: 1, padding: 10, borderRadius: 10, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>Eliminar</button>
            </div>
          </>
        )}

        <div style={{ marginTop: 16, fontSize: 11, opacity: 0.75 }}>
          Dica: os elementos usam mm e são convertidos em HTML com posicionamento absoluto. O backend usa o Body HTML na impressão.
        </div>
      </div>
    </div>
  );
}
